import type User from '@alltrails/core/types/User';
import { type IntlShape } from 'react-intl';
import Logo from '@alltrails/denali/components/Logo';
import type { LanguageRegionCode } from '@alltrails/core/types/LanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/shared/utils/languageSupport';
import { getCopyrightText } from '@alltrails/core';
import AppForOutdoors from './AppForTheOutdoors';
import styles from './Footer.module.scss';
import MembersForThePlanet from './MembersForThePlanet';
import ConnectWithUs from './ConnectWithUs';
import FooterLinks from './FooterLinks';
import ManageCookies from './ManageCookies';
import LanguageSelectDropdown from './LanguageSelectDropdown';

const enabledLanguages = [
  'en-US',
  'en-GB',
  'da-DK',
  'de-DE',
  'es-ES',
  'es-MX',
  'fr-FR',
  'it-IT',
  'nl-NL',
  'nb-NO',
  'pl-PL',
  'pt-BR',
  'pt-PT',
  'sv-SE'
];

export type Props = {
  user?: User | null;
  wideFooter: boolean;
  languageRegionCode: LanguageRegionCode;
  noFollowSocials?: boolean;
  noReferrerAppLinks?: boolean;
  irclickid?: string;
  intl: IntlShape;
  pageReloadOnLanguageChange?: boolean;
  hasByotExperiment?: boolean;
};

const Footer = ({
  user,
  wideFooter,
  languageRegionCode,
  noFollowSocials,
  noReferrerAppLinks,
  irclickid,
  intl,
  pageReloadOnLanguageChange,
  hasByotExperiment
}: Props) => {
  return (
    <footer className={`${styles.container} ${wideFooter ? styles.wideContainer : ''}`}>
      <div className={`${styles.innerContainer} ${wideFooter ? styles.wideInnerContainer : ''}`}>
        <Logo size="sm" color="colored-light" variant="logomark" />
        <FooterLinks
          intl={intl}
          languageRegionCode={languageRegionCode}
          user={user}
          irclickid={irclickid}
          hasByotExperiment={hasByotExperiment}
        />
        <div className={styles.panelsContainer}>
          <div className={styles.appForOutdoors}>
            <AppForOutdoors languageRegionCode={languageRegionCode} noReferrerAppLinks={noReferrerAppLinks} intl={intl} />
          </div>
          <div className={styles.membersContainer}>
            <MembersForThePlanet intl={intl} />
          </div>
          <div className={styles.connectWithUs}>
            <ConnectWithUs noFollowSocials={noFollowSocials} intl={intl} />
          </div>
        </div>
        <div className={styles.bottomText}>
          <div className={styles.footNotes}>
            <div>
              <div className={styles.copyright}>{getCopyrightText({ intl })}</div>
              <div>
                {intl.formatMessage({
                  defaultMessage:
                    'ALLTRAILS® and the AllTrails Mountain Design are registered trademarks of AllTrails, LLC in the United States as well as certain other jurisdictions.'
                })}
              </div>
              <div>{intl.formatMessage({ defaultMessage: 'FIND YOUR OUTSIDE™ is a trademark of AllTrails, LLC.' })}</div>
            </div>
          </div>
          <div className={styles.languageSelect}>
            <LanguageSelectDropdown
              languageRegionCode={languageRegionCode}
              enabledLanguages={enabledLanguages}
              pageReloadOnLanguageChange={pageReloadOnLanguageChange}
            />
          </div>
        </div>
        <div className={styles.userLinks}>
          <a href={wrapUrlSafe('/privacy', languageRegionCode)} rel="nofollow">
            {intl.formatMessage({ defaultMessage: 'Privacy Policy' })}
          </a>
          •
          <a href={wrapUrlSafe('/terms', languageRegionCode)} rel="nofollow">
            {intl.formatMessage({ defaultMessage: 'Terms' })}
          </a>
          •
          <a href={wrapUrlSafe('/cookie_policy', languageRegionCode)} rel="nofollow">
            {intl.formatMessage({ defaultMessage: 'Cookie Policy' })}
          </a>
          <ManageCookies />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
